import { Box, InlineNotification, Typography } from '@packages/shared';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Edit32, EnterConfirmSubmit32 } from '@packages/themes/icons';
import type { InstallmentDataQuery } from '@packages/gql/generated/shopping/graphql';
import { getNormalizedValue } from './getNormalizedValue';
import { editableBasePriceMessages } from './messages';
import type { InstallmentsCalulatorCalculationValuta } from '../../types';

type InstallmentData = NonNullable<InstallmentDataQuery['installment']>;

export type EditableBasePriceProps = Pick<InstallmentData, 'currency' | 'minValue' | 'maxValue'> & {
  /**
   * the initial base price in Euro as float (e.g. 13.37)
   */
  calculationValuta: InstallmentsCalulatorCalculationValuta;
  /**
   * handler function that will be executed after input
   *
   * @price - input price in Euro as float (e.g. 13.37)
   */
  onChangeHandler: (price: number) => void;
};

type Mode = 'EDIT' | 'DISPLAY';

const iconSize = '20px';

export const EditableBasePrice = ({
  calculationValuta,
  currency,
  onChangeHandler,
  minValue,
  maxValue,
}: EditableBasePriceProps) => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useFormatPrice();
  const [mode, setMode] = useState<Mode>('DISPLAY');
  const [error, setError] = useState<null | string>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && mode === 'EDIT') {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [mode, inputRef]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (error !== null) {
      timer = setTimeout(() => {
        setError(null);
        clearTimeout(timer);
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [error, setError]);

  const setEditMode = () => {
    setMode('EDIT');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInput = () => {
    if (inputRef.current) {
      let value = getNormalizedValue(inputRef.current.value);

      // check if input is smaller then minValue given from the calculation result
      if (value < minValue) {
        value = minValue;
        setError(
          formatMessage(editableBasePriceMessages.notificationAutoSetMin, {
            minValue: formatPrice(minValue, currency, true),
          }),
        );
      }
      // check if input is smaller then maxValue given from the calculation result
      else if (maxValue > 0 && value > maxValue) {
        value = maxValue;
        setError(
          formatMessage(editableBasePriceMessages.notificationAutoSetMinMax, {
            minValue: formatPrice(minValue, currency, true),
            maxValue: formatPrice(maxValue, currency, true),
          }),
        );
      }

      inputRef.current.value = formatPrice(value, currency, true);
      setMode('DISPLAY');
      onChangeHandler(value);
    }
  };

  return (
    <Box>
      {error !== null && (
        <InlineNotification severity="error" onClose={() => setError(null)}>
          {error}
        </InlineNotification>
      )}
      <Box
        sx={{
          py: 1,
          px: iconSize,
          backgroundColor: mode === 'DISPLAY' ? 'transparent' : 'common.white',
          position: 'relative',
          maxWidth: '11rem',
          mx: 'auto',
        }}
      >
        <Typography variant="h4">
          <input
            defaultValue={formatPrice(calculationValuta, currency, true)}
            style={{
              display: 'block',
              width: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              textAlign: 'center',
              font: 'inherit',
              color: 'inherit',
            }}
            disabled={mode === 'DISPLAY'}
            ref={inputRef}
            onBlur={handleInput}
            onKeyUp={(event) => event.key === 'Enter' && event.currentTarget.blur()}
          />
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: iconSize,
            height: iconSize,
            cursor: 'pointer',
          }}
        >
          {mode === 'DISPLAY' && (
            <Edit32 sx={{ width: '100%', height: '100%' }} onClick={setEditMode} />
          )}
          {mode === 'EDIT' && (
            <EnterConfirmSubmit32 sx={{ width: '100%', height: '100%' }} onClick={handleInput} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
